import React, { useState, useCallback } from "react";
import { TEMPLATE_MAP } from "../templates/index";
import IntroGate from "../templates/shared/IntroGate";
import ProgressBar from "../templates/shared/ProgressBar";
import ReflectionScreen from "./ReflectionScreen";
import CompletionScreen from "./CompletionScreen";
import { completeSession } from "../../services/sessionService";
import MuteButton from "./MuteButton";
import { useTemplateAudio } from "../../hooks/useTemplateAudio";
import { TEMPLATE_AUDIO } from "../templates/shared/audioConfig";
import "../../css2/ActivityShell.css";
import "../../css2/activity-templates.css";

const TEMPLATE_ATMOSPHERES = {
  breathing:           "#2A1F3D",
  pmr:                 "#1A2030",
  visualization:       "#171B30",
  grounding:           "#1A2015",
  journaling:          "#221F18",
  focus_anchoring:     "#1A1C2E",
  creative_expression: "#2A152E",
};

// ActivityShell owns the full exercise lifecycle.
//
// Phases: "intro" → "exercise" → "reflection" → "completion"
//
// "Reflect now" is always visible during "exercise" — the user can conclude
// at any point. Exercise auto-completing also moves to "reflection".
//
// Props:
//   exercise   — { id, title, subtitle, template, config }
//   sessionId  — from parent after createSession (may be null if API failed)
//   onClose    — called when the user exits at any phase
//   onDone     — called when the user finishes the completion screen

export default function ActivityShell({ exercise, sessionId, onClose, onDone }) {
  const [phase, setPhase] = useState("intro");
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");

  const TemplateComponent = TEMPLATE_MAP[exercise.template];

  const trackSrc = TEMPLATE_AUDIO[exercise.template] ?? null;
  const { isMuted, toggleMute, start, stop } = useTemplateAudio(trackSrc);

  const handleBegin = useCallback(() => {
    if (phase !== "intro") return; // guard against double-tap
    start();
    setPhase("exercise");
  }, [phase, start]);

  const handleReflectionSubmit = () => setPhase("completion");

  const handleProgress = useCallback((value, label = "") => {
    setProgress(value);
    setProgressLabel(label);
  }, []);

  // Both "Reflect now" and auto-complete go through here — stop() and the
  // background fade are handled in one place instead of duplicated in callers.
  const goToReflection = useCallback(() => {
    completeSession(sessionId).catch((err) =>
      console.error("Failed to complete session:", err)
    );
    stop();
    setIsLeaving(true);
    setTimeout(() => setPhase("reflection"), 500);
  }, [sessionId, stop]);

  const handleReflect = () => goToReflection();

  const handleComplete = useCallback(() => {
    setProgress(1);
    setProgressLabel("Complete");
    goToReflection();
  }, [goToReflection]);

  if (phase === "reflection") {
    return (
      <ReflectionScreen
        sessionId={sessionId}
        onClose={onClose}
        onSubmit={handleReflectionSubmit}
      />
    );
  }

  if (phase === "completion") {
    return (
      <CompletionScreen
        exerciseTitle={exercise.title}
        onClose={onClose}
        onDone={onDone}
      />
    );
  }

  return (
    <div
      className="activity-shell"
      data-phase={phase}
      style={{
        backgroundColor: (() => {
          if (phase !== "exercise" || isLeaving) return "#FBF8EB";
          const atm = TEMPLATE_ATMOSPHERES[exercise.template];
          if (!atm) console.warn(`[ActivityShell] Unknown template: "${exercise.template}"`);
          return atm ?? "#12101A";
        })(),
      }}
    >
      {phase === "intro" && (
        <IntroGate
          intro={{
            title: exercise.title,
            subtitle: exercise.subtitle,
            intention: "Take a moment to arrive",
          }}
          onBegin={handleBegin}
        />
      )}

      <div
        className="activity-shell__content"
        style={{
          opacity: phase === "intro" || isLeaving ? 0 : 1,
          pointerEvents: phase === "intro" || isLeaving ? "none" : "auto",
        }}
      >
        <header className="activity-shell__header">
          <button
            className="activity-shell__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
          <button className="activity-shell__reflect" onClick={handleReflect}>
            Reflect now
          </button>
        </header>

        <p className="activity-shell__title">{exercise.title}</p>

        <div className="activity-shell__template">
          {TemplateComponent ? (
            <TemplateComponent
              config={exercise.config}
              onProgress={handleProgress}
              onComplete={handleComplete}
            />
          ) : (
            <p className="activity-shell__no-template">
              Exercise type "{exercise.template}" not found.
            </p>
          )}
        </div>

        <div className="activity-shell__progress-wrap">
          <ProgressBar progress={progress} label={progressLabel} />
        </div>
      </div>

      {trackSrc && phase === "exercise" && !isLeaving && (
        <MuteButton isMuted={isMuted} onToggle={toggleMute} />
      )}
    </div>
  );
}
